import { buffers, Channel } from 'redux-saga';
import {
	actionChannel,
	call,
	flush,
	put,
	race,
	select,
	take,
	takeLatest,
} from 'redux-saga/effects';

import {
	getFeaturedAchievements,
	getPlayerAchievements,
	SvaraAchievementShowcaseResponse,
	SvaraFeaturedAchievementsResponse,
	SvaraPlayerAchievementsResponse,
	updateFeaturedAchievements,
} from '../../services/api';
import { getUserFacingHttpErrorKey } from '../../utils/helpers/httpErrorKey';
import { loginSuccess, logout } from '../auth/authActions';
import {
	achievementShowcaseRevert,
	achievementShowcaseSaveFailure,
	achievementShowcaseSaveStarted,
	achievementShowcaseSaveSuccess,
	achievementShowcaseToggle,
	achievementsHubFailure,
	achievementsHubRequest,
	achievementsHubSuccess,
	achievementsPlayerFeaturedFailure,
	achievementsPlayerFeaturedRequest,
	achievementsPlayerFeaturedSuccess,
} from './achievementsActions';
import { selectFeaturedFamilyKeys, selectShowcaseVersion } from './achievementsReducer';

const LOAD_ERROR_KEY = 'T_SITE_ACHIEVEMENTS_ERROR_LOAD';
const SAVE_ERROR_KEY = 'T_SITE_ACHIEVEMENTS_ERROR_SAVE';

const SERVER_CODE_KEYS: Record<string, string> = {
	svara_achievement_showcase_stale: 'T_SITE_ACHIEVEMENTS_ERROR_STALE',
	svara_achievement_not_unlocked: 'T_SITE_ACHIEVEMENTS_ERROR_NOT_UNLOCKED',
};

type ToggleAction = ReturnType<typeof achievementShowcaseToggle>;

const serverCodeOf = (error: any): string | undefined => {
	const body = error?.response?.data;
	if (!body || typeof body !== 'object') return undefined;

	const message = body.message;
	return typeof message === 'string' ? message : undefined;
};

const achievementErrorKey = (error: any, fallbackKey: string): string => {
	const code = serverCodeOf(error);
	if (code && SERVER_CODE_KEYS[code]) return SERVER_CODE_KEYS[code];

	const resolved = getUserFacingHttpErrorKey(error, {
		serverUnavailableKey: fallbackKey,
		authKey: fallbackKey,
		networkKey: fallbackKey,
		clientErrorKey: fallbackKey,
		fallbackKey,
	});

	return resolved.startsWith('T_') ? resolved : fallbackKey;
};

function* fetchAchievementsHubWorker() {
	try {
		const outcome: { data?: SvaraPlayerAchievementsResponse } = yield race({
			data: call(getPlayerAchievements),
			sessionChanged: take([logout.type, loginSuccess.type]),
		});
		if (outcome.data) yield put(achievementsHubSuccess(outcome.data));
	} catch (error: any) {
		yield put(achievementsHubFailure(achievementErrorKey(error, LOAD_ERROR_KEY)));
	}
}

function* fetchPlayerFeaturedWorker(action: ReturnType<typeof achievementsPlayerFeaturedRequest>) {
	const playerId = action.payload;

	try {
		const outcome: { data?: SvaraFeaturedAchievementsResponse } = yield race({
			data: call(getFeaturedAchievements, playerId),
			sessionChanged: take([logout.type, loginSuccess.type]),
		});
		if (outcome.data) {
			yield put(
				achievementsPlayerFeaturedSuccess({ playerId, featured: outcome.data.featured ?? [] }),
			);
		}
	} catch {
		yield put(achievementsPlayerFeaturedFailure(playerId));
	}
}

function* saveShowcase(toggles: Channel<ToggleAction>) {
	yield flush(toggles);
	yield put(achievementShowcaseSaveStarted());

	const familyKeys: string[] = yield select(selectFeaturedFamilyKeys);
	const expectedVersion: number = yield select(selectShowcaseVersion);

	try {
		const data: SvaraAchievementShowcaseResponse = yield call(
			updateFeaturedAchievements,
			familyKeys,
			expectedVersion,
		);
		yield put(
			achievementShowcaseSaveSuccess({ version: data.version, featuredFamilyKeys: familyKeys }),
		);
	} catch (error: any) {
		yield flush(toggles);
		yield put(achievementShowcaseSaveFailure({ key: achievementErrorKey(error, SAVE_ERROR_KEY) }));
		yield call(fetchAchievementsHubWorker);
		yield flush(toggles);
		yield put(achievementShowcaseRevert());
	}
}

function* showcaseSaveLoop() {
	const toggles: Channel<ToggleAction> = yield actionChannel(
		achievementShowcaseToggle.type,
		buffers.expanding<ToggleAction>(),
	);

	while (true) {
		yield take(toggles);

		const outcome: { logout?: unknown } = yield race({
			save: call(saveShowcase, toggles),
			logout: take(logout.type),
		});
		if (outcome.logout) yield flush(toggles);
	}
}

export function* achievementsRootSaga() {
	yield takeLatest(achievementsHubRequest.type, fetchAchievementsHubWorker);
	yield takeLatest(achievementsPlayerFeaturedRequest.type, fetchPlayerFeaturedWorker);
	yield call(showcaseSaveLoop);
}
